/**
 * Signal panel: entry, stop, targets, confidence, and the reasons.
 *
 * The reason list is the point of the whole platform -- a signal you cannot
 * explain is a signal you cannot tune. Factors arrive from the API already
 * ordered by contribution, so the top three lines are the three things that
 * actually drove the decision.
 */

import type { RiskFlag, Signal, SignalFactor } from '@/api/types'
import { useMtf, useRiskFlags, useSignals } from '@/hooks/useQueries'
import { useAppStore } from '@/store/useAppStore'
import { TIMEFRAME_HORIZON } from '@/theme/chart'
import { formatPrice, regimeLabel } from '@/components/Chart/ChartPanel'
import { ConfluenceBadge } from './ConfluenceBadge'

export function SignalPanel({ ticker }: { ticker: string | null }) {
  const timeframe = useAppStore((s) => s.timeframe)
  const signalsQuery = useSignals(ticker, timeframe)
  const mtfQuery = useMtf(ticker)
  const riskQuery = useRiskFlags(ticker)

  const signal = signalsQuery.data?.[0] ?? null
  const horizon = TIMEFRAME_HORIZON[timeframe]
  const consensus = mtfQuery.data?.find((row) => row.horizon === horizon) ?? null

  return (
    <aside className="flex w-80 shrink-0 flex-col overflow-y-auto border-l border-border bg-panel-alt">
      <header className="flex items-baseline justify-between border-b border-border px-3 py-2">
        <span className="text-2xs font-semibold uppercase tracking-wider text-ink-dim">Tín hiệu</span>
        <span className="text-2xs text-ink-dim">{timeframe} · {horizon ?? '—'}</span>
      </header>

      <div className="space-y-2 p-2">
        {/* Risk first: a pump-dump warning is the one thing that must be
            read before the trade idea underneath it. */}
        <RiskFlags flags={riskQuery.data ?? []} />
        <ConfluenceBadge consensus={consensus} />

        {!ticker && <Empty text="Chọn một mã để xem tín hiệu" />}
        {ticker && signalsQuery.isLoading && <Empty text="Đang tải…" />}
        {ticker && !signalsQuery.isLoading && !signal && (
          <Empty text={`Không có tín hiệu đang mở ở khung ${timeframe}`} />
        )}

        {signal && <SignalCard signal={signal} />}
      </div>
    </aside>
  )
}

function RiskFlags({ flags }: { flags: RiskFlag[] }) {
  if (flags.length === 0) return null

  const labels: Record<string, string> = {
    pump_dump: 'Nghi vấn pump-dump',
    low_liquidity: 'Thanh khoản thấp',
    atc_manipulation: 'Bất thường phiên ATC',
    ceiling_streak: 'Chuỗi phiên trần',
  }

  return (
    <div className="space-y-1">
      {flags.slice(0, 3).map((flag) => (
        <div
          key={flag.id}
          className={`rounded border p-2 text-2xs ${
            flag.severity >= 3
              ? 'border-down/60 bg-down/15 text-down'
              : 'border-warn/60 bg-warn/10 text-warn'
          }`}
        >
          <div className="font-medium">
            🚨 {labels[flag.flag_type] ?? flag.flag_type} · mức {flag.severity}/3
          </div>
          {flag.human_text && <div className="mt-0.5 text-ink/80">{flag.human_text}</div>}
          <div className="mt-0.5 text-ink-dim">Cảnh báo tránh xa, không phải tín hiệu bán.</div>
        </div>
      ))}
    </div>
  )
}

function SignalCard({ signal }: { signal: Signal }) {
  const isLong = signal.side === 'long'
  const wanted = isLong ? 1 : -1
  const supporting = signal.factors.filter((f) => f.direction === wanted)
  const opposing = signal.factors.filter((f) => f.direction === -wanted)
  const neutral = signal.factors.filter((f) => f.direction === 0)

  return (
    <article className="space-y-2 rounded border border-border bg-panel p-2">
      <div className="flex items-center justify-between">
        <span
          className={`rounded px-2 py-0.5 text-xs font-semibold ${
            isLong ? 'bg-up/20 text-up' : 'bg-down/20 text-down'
          }`}
        >
          {isLong ? 'MUA' : 'BÁN'}
        </span>
        <span className="text-2xs text-ink-dim">
          {signal.status} · rev {signal.revision}
        </span>
      </div>

      <ConfidenceMeter value={signal.confidence} />

      <dl className="grid grid-cols-2 gap-x-3 gap-y-1 text-2xs">
        <Stat label="Vùng vào lệnh">
          {formatPrice(signal.entry_low)} – {formatPrice(signal.entry_high)}
        </Stat>
        <Stat label="Cắt lỗ" tone="down">{formatPrice(signal.stop_loss)}</Stat>
        <Stat label="R:R">{signal.rr_ratio.toFixed(2)}</Stat>
        <Stat label="Trạng thái TT">{regimeLabel(signal.regime ?? '')}</Stat>
        <Stat label="Nắm giữ">{signal.hold_hint ?? '—'}</Stat>
        <Stat label="Khung">{signal.timeframe}</Stat>
      </dl>

      <div>
        <h4 className="mb-1 text-2xs uppercase tracking-wider text-ink-dim">Chốt lời từng phần</h4>
        <ul className="space-y-0.5">
          {signal.targets.map((target) => (
            <li key={target.idx} className="flex items-center justify-between text-2xs">
              <span className="text-ink-dim">
                TP{target.idx} · {target.size_pct.toFixed(0)}%
                {target.basis && <span className="ml-1 opacity-60">({target.basis})</span>}
              </span>
              <span className={`font-mono ${target.hit_at ? 'text-up line-through' : 'text-ink'}`}>
                {formatPrice(target.price)}
              </span>
            </li>
          ))}
        </ul>
      </div>

      <FactorList title="Yếu tố đồng thuận" factors={supporting} tone="up" />
      {opposing.length > 0 && <FactorList title="Yếu tố ngược chiều" factors={opposing} tone="down" />}
      {neutral.length > 0 && (
        <details className="text-2xs">
          <summary className="cursor-pointer text-ink-dim hover:text-ink">
            {neutral.length} chỉ báo trung tính
          </summary>
          <FactorList title="" factors={neutral} tone="dim" />
        </details>
      )}

      {signal.invalidation && (
        <p className="rounded bg-panel-alt p-1.5 text-2xs text-ink-dim">
          <span className="text-ink">Huỷ tín hiệu khi:</span> {signal.invalidation}
        </p>
      )}
    </article>
  )
}

function ConfidenceMeter({ value }: { value: number }) {
  const tone = value >= 75 ? 'bg-up' : value >= 60 ? 'bg-accent' : 'bg-warn'
  return (
    <div>
      <div className="mb-0.5 flex justify-between text-2xs">
        <span className="text-ink-dim">Độ tin cậy</span>
        <span className="font-mono text-ink">{value.toFixed(1)}</span>
      </div>
      <div className="h-1.5 overflow-hidden rounded bg-panel-alt">
        <div className={`h-full ${tone}`} style={{ width: `${Math.min(100, value)}%` }} />
      </div>
    </div>
  )
}

function FactorList({
  title, factors, tone,
}: {
  title: string
  factors: SignalFactor[]
  tone: 'up' | 'down' | 'dim'
}) {
  if (factors.length === 0) return null
  const color = tone === 'up' ? 'text-up' : tone === 'down' ? 'text-down' : 'text-ink-dim'
  const mark = tone === 'up' ? '▲' : tone === 'down' ? '▼' : '•'

  return (
    <div>
      {title && <h4 className="mb-1 text-2xs uppercase tracking-wider text-ink-dim">{title}</h4>}
      <ul className="space-y-1">
        {factors.map((factor) => (
          <li key={factor.strategy_key} className="flex gap-1.5 text-2xs leading-snug">
            <span className={color}>{mark}</span>
            <span className="flex-1 text-ink/90">
              {factor.human_text}
              <span className="ml-1 text-ink-dim opacity-70">
                ({factor.strategy_key} · {(factor.score * factor.weight).toFixed(2)})
              </span>
            </span>
          </li>
        ))}
      </ul>
    </div>
  )
}

function Stat({ label, children, tone }: { label: string; children: React.ReactNode; tone?: 'down' }) {
  return (
    <div>
      <dt className="text-ink-dim">{label}</dt>
      <dd className={`font-mono ${tone === 'down' ? 'text-down' : 'text-ink'}`}>{children}</dd>
    </div>
  )
}

function Empty({ text }: { text: string }) {
  return <p className="rounded border border-border bg-panel p-3 text-center text-2xs text-ink-dim">{text}</p>
}
